import { useState, useEffect, useMemo } from 'react'
import {
	Search,
	Filter,
	FileText,
	ChevronLeft,
	ChevronRight,
	Tag,
	BookOpen,
	X,
} from 'lucide-react'
import Layout from '@/components/layout/layout'
import CustomersEntity from '@/entities/api/customers/customer'
import { TermsDocument, termsDocuments } from '@/utils/terms-documents'

const TermsAndConditions = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const [searchTerm, setSearchTerm] = useState('')
	const [selectedCategory, setSelectedCategory] = useState('all')
	const [currentPage, setCurrentPage] = useState(1)
	const [isVisible, setIsVisible] = useState(false)
	const [selectedDocument, setSelectedDocument] =
		useState<TermsDocument | null>(null)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const itemsPerPage = 6

	const categories = [
		{
			value: 'all',
			label: 'Todos los documentos',
			count: termsDocuments.length,
		},
		{
			value: 'general',
			label: 'Términos Generales',
			count: termsDocuments.filter((doc) => doc.category === 'general').length,
		},
		{
			value: 'privacy',
			label: 'Privacidad',
			count: termsDocuments.filter((doc) => doc.category === 'privacy').length,
		},
		{
			value: 'payment',
			label: 'Pagos',
			count: termsDocuments.filter((doc) => doc.category === 'payment').length,
		},
		{
			value: 'shipping',
			label: 'Envíos',
			count: termsDocuments.filter((doc) => doc.category === 'shipping').length,
		},
		{
			value: 'returns',
			label: 'Devoluciones',
			count: termsDocuments.filter((doc) => doc.category === 'returns').length,
		},
		{
			value: 'user',
			label: 'Usuario',
			count: termsDocuments.filter((doc) => doc.category === 'user').length,
		},
		{
			value: 'contact',
			label: 'Contacto',
			count: termsDocuments.filter((doc) => doc.category === 'contact').length,
		},
		{
			value: 'legal',
			label: 'Legal',
			count: termsDocuments.filter((doc) => doc.category === 'legal').length,
		},
	]

	useEffect(() => {
		setIsVisible(true)
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsModalOpen(false)
				setIsDropdownOpen(false)
			}
		}

		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [])

	const filteredDocuments = useMemo(() => {
		return termsDocuments.filter((doc) => {
			const matchesSearch =
				doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
				doc.content.toLowerCase().includes(searchTerm.toLowerCase())
			const matchesCategory =
				selectedCategory === 'all' || doc.category === selectedCategory
			return matchesSearch && matchesCategory
		})
	}, [searchTerm, selectedCategory])

	const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage)
	const startIndex = (currentPage - 1) * itemsPerPage
	const paginatedDocuments = filteredDocuments.slice(
		startIndex,
		startIndex + itemsPerPage
	)

	useEffect(() => {
		setCurrentPage(1)
	}, [searchTerm, selectedCategory])

	const openDocumentModal = (document: TermsDocument) => {
		setSelectedDocument(document)
		setIsModalOpen(true)
	}

	const closeDocumentModal = () => {
		setIsModalOpen(false)
		setSelectedDocument(null)
	}

	const handleCategorySelect = (categoryValue: string) => {
		setSelectedCategory(categoryValue)
		setIsDropdownOpen(false)
	}

	return (
		<Layout customer={customer} token={token}>
			<div className='min-h-screen bg-white'>
				<div className='max-w-full mx-auto flex flex-col items-center'>
					<section className='w-full bg-gradient-to-br from-white via-gray-50/30 to-white relative overflow-hidden'>
						<div className='absolute inset-0 opacity-5'>
							<div
								className='absolute inset-0'
								style={{
									backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
								}}
							/>
						</div>

						<div className='relative max-w-7xl mx-auto px-6 py-20 lg:py-32'>
							<div
								className={`text-center transform transition-all duration-1200 ease-out ${
									isVisible
										? 'translate-y-0 opacity-100'
										: 'translate-y-16 opacity-0'
								}`}
							>
								<div className='inline-flex items-center gap-3 bg-red-50 px-6 py-3 rounded-full mb-8'>
									<FileText className='w-5 h-5 text-red-600' />
									<span className='text-red-600 font-bold text-sm uppercase tracking-wide'>
										Documentos Legales
									</span>
								</div>

								<h1 className='text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-gray-900 leading-tight mb-8 tracking-tight'>
									Términos y
									<span className='relative ml-4'>
										<span className='text-red-600'>Condiciones</span>
										<div className='absolute -bottom-3 left-0 w-full h-5 bg-gradient-to-r from-red-100 to-red-200 -z-10 transform -skew-x-12 rounded-sm'></div>
									</span>
								</h1>

								<p className='text-gray-600 text-lg lg:text-xl xl:text-2xl max-w-4xl mx-auto font-medium leading-relaxed'>
									Toda la información legal importante sobre el uso de
									SportCenter Shop. Manténgase informado sobre nuestras
									políticas y procedimientos.
								</p>
								<div className='w-28 h-1.5 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full mt-8'></div>
							</div>
						</div>
					</section>

					<section className='w-full px-6 -mt-16 relative z-10'>
						<div className='max-w-7xl mx-auto'>
							<div className='bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 lg:p-12 backdrop-blur-sm'>
								<div className='flex flex-col xl:flex-row gap-8 items-start xl:items-center'>
									<div className='flex-1 relative'>
										<div className='absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none'>
											<Search className='h-6 w-6 text-gray-400' />
										</div>
										<input
											type='text'
											placeholder='Buscar en términos y condiciones...'
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											className='w-full pl-16 pr-6 py-5 text-base border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300 bg-gray-50/50 hover:bg-white hover:border-gray-300 font-medium'
										/>
									</div>

									<div className='relative w-full xl:w-96'>
										<div className='absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none z-10'>
											<Filter className='h-6 w-6 text-gray-400' />
										</div>
										<button
											onClick={() => setIsDropdownOpen(!isDropdownOpen)}
											className='w-full pl-16 pr-12 py-5 text-base border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300 bg-gray-50/50 hover:bg-white hover:border-gray-300 font-medium text-left flex items-center justify-between'
										>
											<span>
												{
													categories.find(
														(cat) => cat.value === selectedCategory
													)?.label
												}{' '}
												(
												{
													categories.find(
														(cat) => cat.value === selectedCategory
													)?.count
												}
												)
											</span>
											<ChevronRight
												className={`h-6 w-6 text-gray-400 transition-transform duration-200 ${
													isDropdownOpen ? 'rotate-90' : ''
												}`}
											/>
										</button>

										{isDropdownOpen && (
											<div className='absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden'>
												{categories.map((category) => (
													<button
														key={category.value}
														onClick={() => handleCategorySelect(category.value)}
														className={`w-full px-6 py-4 text-left hover:bg-red-50 transition-colors duration-200 flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
															selectedCategory === category.value
																? 'bg-red-50 text-red-600 font-bold'
																: 'text-gray-700 font-medium'
														}`}
													>
														<span>{category.label}</span>
														<span
															className={`text-sm px-2 py-1 rounded-full ${
																selectedCategory === category.value
																	? 'bg-red-100 text-red-600'
																	: 'bg-gray-100 text-gray-500'
															}`}
														>
															{category.count}
														</span>
													</button>
												))}
											</div>
										)}
									</div>
								</div>

								<div className='mt-8 pt-8 border-t border-gray-100'>
									<div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-base text-gray-600'>
										<span className='font-medium'>
											Mostrando{' '}
											<span className='font-bold text-gray-900'>
												{paginatedDocuments.length}
											</span>{' '}
											de{' '}
											<span className='font-bold text-gray-900'>
												{filteredDocuments.length}
											</span>{' '}
											documentos
										</span>
										{searchTerm && (
											<div className='flex items-center gap-3'>
												<span>Resultados para:</span>
												<span className='bg-red-50 text-red-600 px-4 py-2 rounded-full font-bold text-sm'>
													"{searchTerm}"
												</span>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</section>

					<section className='w-full px-6 py-24'>
						<div className='max-w-7xl mx-auto'>
							{filteredDocuments.length === 0 ? (
								<div className='text-center py-24'>
									<div className='w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-8'>
										<Search className='w-16 h-16 text-gray-400' />
									</div>
									<h3 className='text-2xl font-black text-gray-900 mb-4 tracking-tight'>
										No se encontraron documentos
									</h3>
									<p className='text-gray-600 text-lg font-medium'>
										Intenta ajustar tus filtros de búsqueda
									</p>
								</div>
							) : (
								<>
									<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10'>
										{paginatedDocuments.map((doc) => {
											const IconComponent = doc.icon
											return (
												<div
													key={doc.id}
													className='group bg-white rounded-3xl shadow-lg hover:shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 hover:-translate-y-2'
												>
													<div
														className={`bg-gradient-to-br ${doc.color} p-8 relative overflow-hidden`}
													>
														<div className='absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20'></div>
														<div className='absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16'></div>

														<div className='relative z-10 flex items-start gap-4'>
															<div className='w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg'>
																<IconComponent className='w-8 h-8 text-white' />
															</div>
															<div className='flex-1'>
																<h3 className='text-white font-black text-xl xl:text-2xl leading-tight tracking-tight'>
																	{doc.title}
																</h3>
																<div className='flex items-center gap-2 mt-3'>
																	<Tag className='w-4 h-4 text-white/80' />
																	<span className='text-white/90 text-sm font-bold capitalize'>
																		{
																			categories.find(
																				(cat) => cat.value === doc.category
																			)?.label
																		}
																	</span>
																</div>
															</div>
														</div>
													</div>

													<div className='p-8'>
														<div
															className='text-gray-600 text-base leading-relaxed mb-8 font-medium'
															dangerouslySetInnerHTML={{
																__html: doc.content.substring(0, 180) + '...',
															}}
														/>

														<button
															onClick={() => openDocumentModal(doc)}
															className='w-full cursor-pointer bg-gradient-to-r from-gray-50 to-gray-100 hover:from-red-50 hover:to-red-100 text-gray-700 hover:text-red-600 font-black py-4 px-8 rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 group-hover:shadow-lg border border-gray-200 hover:border-red-200 text-base hover:scale-105'
														>
															<BookOpen className='w-5 h-5' />
															<span>Leer documento completo</span>
														</button>
													</div>
												</div>
											)
										})}
									</div>

									{totalPages > 1 && (
										<div className='flex justify-center items-center mt-20 gap-3'>
											<button
												onClick={() =>
													setCurrentPage((prev) => Math.max(prev - 1, 1))
												}
												disabled={currentPage === 1}
												className='p-4 rounded-2xl border-2 border-gray-200 hover:border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg'
											>
												<ChevronLeft className='w-6 h-6' />
											</button>

											{Array.from({ length: totalPages }, (_, i) => i + 1).map(
												(page) => (
													<button
														key={page}
														onClick={() => setCurrentPage(page)}
														className={`w-14 h-14 rounded-2xl font-black transition-all duration-300 text-base ${
															currentPage === page
																? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-xl transform scale-110'
																: 'border-2 border-gray-200 hover:border-red-200 hover:bg-red-50 text-gray-700 hover:shadow-lg'
														}`}
													>
														{page}
													</button>
												)
											)}

											<button
												onClick={() =>
													setCurrentPage((prev) =>
														Math.min(prev + 1, totalPages)
													)
												}
												disabled={currentPage === totalPages}
												className='p-4 rounded-2xl border-2 border-gray-200 hover:border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg'
											>
												<ChevronRight className='w-6 h-6' />
											</button>
										</div>
									)}
								</>
							)}
						</div>
					</section>
				</div>

				{isModalOpen && selectedDocument && (
					<div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
						<div className='bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl'>
							<div
								className={`bg-gradient-to-r ${selectedDocument.color} p-8 relative overflow-hidden`}
							>
								<div className='absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20'></div>
								<div className='absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16'></div>

								<div className='relative z-10 flex items-center justify-between'>
									<div className='flex items-center gap-4'>
										<div className='w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg'>
											<selectedDocument.icon className='w-8 h-8 text-white' />
										</div>
										<div>
											<h2 className='text-white font-black text-2xl xl:text-3xl leading-tight tracking-tight'>
												{selectedDocument.title}
											</h2>
										</div>
									</div>
									<button
										onClick={closeDocumentModal}
										className='w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center text-white transition-all duration-200 hover:scale-110'
									>
										<X className='w-6 h-6' />
									</button>
								</div>
							</div>

							<div className='p-8 overflow-y-auto max-h-[60vh]'>
								<div
									className='prose prose-lg max-w-none text-gray-700 leading-relaxed'
									dangerouslySetInnerHTML={{ __html: selectedDocument.content }}
								/>
							</div>

							<div className='p-8 pt-4 border-t border-gray-100 bg-gray-50/50'>
								<div className='flex flex-col sm:flex-row items-center justify-between gap-4'>
									<div className='text-sm text-gray-600'>
										<p className='font-medium'>¿Necesitas más información?</p>
										<p>Contacta con nuestro equipo de soporte</p>
									</div>
									<button
										onClick={closeDocumentModal}
										className='bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg'
									>
										Cerrar documento
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</Layout>
	)
}

export default TermsAndConditions
